import {
  Component,
  computed,
  Input,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSortModule, Sort } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { objectContainsTerm } from 'src/app/shared/utils/object-search.util';
import { IP95UtilizationService } from '../../services/p95-utilization/p95-utilization.service.types';

const COLUMNS = [
  'heat',
  'name_customer_location',
  'dim_connection_id',
  'p95_utilization_rx_pct',
  'p95_utilization_tx_pct',
  'provisioned_speed_rx_mbps',
  'provisioned_speed_tx_mbps',
] as const;

const DEFAULT_PAGE_INDEX = 0;
const DEFAULT_PAGE_SIZE = 10;

type SortOrder = 'ASC' | 'DESC';
type SortField = (typeof COLUMNS)[number];
type SortState = {
  order: SortOrder;
  field: SortField;
};

@Component({
  standalone: true,
  selector: 'app-p95-utilization-list',
  templateUrl: './p95-utilization-list.component.html',
  styleUrl: './p95-utilization-list.component.scss',
  encapsulation: ViewEncapsulation.None,
  imports: [
    MatProgressBarModule,
    MatTableModule,
    MatSortModule,
    MatIconModule,
    MatTooltipModule,
  ],
})
export class P95UtilizationListComponent {
  @Input({ required: true })
  public observations$!: IP95UtilizationService;

  private readonly searchTerm$ = signal('');

  private readonly pageIndex$ = signal(DEFAULT_PAGE_INDEX);

  private readonly pageSize$ = signal(DEFAULT_PAGE_SIZE);

  @Input()
  set searchTerm(value: string) {
    this.searchTerm$.set(value ?? '');
  }

  @Input()
  set pageIndex(value: number) {
    this.pageIndex$.set(Number.isFinite(value) ? value : DEFAULT_PAGE_INDEX);
  }

  @Input()
  set pageSize(value: number) {
    const normalizedValue = Number.isFinite(value) ? value : DEFAULT_PAGE_SIZE;
    this.pageSize$.set(
      normalizedValue > 0 ? normalizedValue : DEFAULT_PAGE_SIZE,
    );
  }

  public readonly orderBy$ = signal<SortState>({
    order: 'DESC',
    field: 'heat',
  });

  public readonly columns = COLUMNS;

  private isSortField(field: string): field is SortField {
    return this.columns.includes(field as SortField);
  }

  private hotnessScore(
    rxValue: number | null | undefined,
    txValue: number | null | undefined,
  ): number {
    const rx = rxValue ?? 0;
    const tx = txValue ?? 0;
    const higher = Math.max(rx, tx);
    const lower = Math.min(rx, tx);

    return higher + 0.5 * lower;
  }

  public readonly filteredRecords$ = computed(() => {
    const records = this.observations$.records$() ?? [];
    const searchTerm = this.searchTerm$().trim();
    const { field, order } = this.orderBy$();

    const filteredRecords =
      searchTerm.length === 0
        ? records
        : records.filter((record) => objectContainsTerm(record, searchTerm));

    const recordsToSort = [...filteredRecords];

    recordsToSort.sort((a, b) => {
      if (field === 'heat') {
        const aHotness = this.hotnessScore(
          a.p95_utilization_rx_pct,
          a.p95_utilization_tx_pct,
        );
        const bHotness = this.hotnessScore(
          b.p95_utilization_rx_pct,
          b.p95_utilization_tx_pct,
        );

        return order === 'ASC' ? aHotness - bHotness : bHotness - aHotness;
      }

      const aValue = a[field];
      const bValue = b[field];

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return order === 'ASC' ? aValue - bValue : bValue - aValue;
      }

      const normalizedA = String(aValue ?? '').toLowerCase();
      const normalizedB = String(bValue ?? '').toLowerCase();
      const comparison = normalizedA.localeCompare(normalizedB, undefined, {
        numeric: true,
      });

      return order === 'ASC' ? comparison : -comparison;
    });

    return recordsToSort;
  });

  public readonly pagedRecords$ = computed(() => {
    const records = this.filteredRecords$();
    const pageSize = this.pageSize$();
    const pageIndex = this.pageIndex$();
    const start = pageIndex * pageSize;

    return records.slice(start, start + pageSize);
  });

  public readonly maxUtilization$ = computed(() => {
    const records = this.filteredRecords$();

    return records.reduce(
      (accumulator, record) => ({
        rx: Math.max(accumulator.rx, record.p95_utilization_rx_pct ?? 0),
        tx: Math.max(accumulator.tx, record.p95_utilization_tx_pct ?? 0),
      }),
      { rx: 0, tx: 0 },
    );
  });

  onMatSortChange(sort: Sort): void {
    const currentSort = this.orderBy$();
    const nextField = this.isSortField(sort.active)
      ? sort.active
      : currentSort.field;

    this.orderBy$.set({
      order: sort.direction === 'asc' ? 'ASC' : 'DESC',
      field: nextField,
    });
  }

  formatPercentage(value: number | null | undefined): string {
    if (value == null) return 'N/A';
    return `${value.toFixed(2)}%`;
  }

  getHeatLevelClass(
    value: number | null | undefined,
    topValue: number = 100,
  ): string {
    if (value == null) return 'heat-level-0';

    const clamped = Math.max(0, value);
    const normalized = topValue > 0 ? Math.min(1, clamped / topValue) : 0;

    if (normalized >= 0.94) return 'heat-level-8';
    if (normalized >= 0.86) return 'heat-level-7';
    if (normalized >= 0.75) return 'heat-level-6';
    if (normalized >= 0.62) return 'heat-level-5';
    if (normalized >= 0.48) return 'heat-level-4';
    if (normalized >= 0.34) return 'heat-level-3';
    if (normalized >= 0.2) return 'heat-level-2';
    return 'heat-level-1';
  }

  formatSpeed(value: number | null | undefined): string {
    if (value == null) return 'N/A';
    return `${value.toFixed(2)} Mbps`;
  }
}
